import { WidgetQuickActions, Widget } from "@components-common/widgets";
import { WidgetConfig } from "../../components-fabric/_types/components";
import { Page } from "@components-common/layouts";
import { Col, Row } from "taltech-styleguide";
import { useEffect, useState } from "react";
import { HttpResponse } from "@dirootie-core/http-types";
import { Link } from "react-router-dom";
import { layoutWorkspace } from "../_common/layout.workspace";
import { studentsAreaRoutes } from "./routes";
import { application } from "../../_runtime/bootstrap";
import { CourseState } from "../../components-domain/education-domain/_types";
import { SimpleTable } from "./SimpleTable";
import { RootieTable } from "../../components-common/tables/RootieTable";
import { useUserContext } from "../../_runtime/iam/session.context";

type ActiveAssignmentsTree = {
  courseId: string;
  courseName: string;
  assignments: {
    assignmentId: string;
    title: string;
  }[];
};

type CourseInfo = {
  courseName: string;
  state: CourseState;
  startDate: Date;
  endDate: Date;
};

const actionsWidget: WidgetConfig & { id: string } = {
  id: "students_shortcuts",
  Component: WidgetQuickActions,
  title: "Shortcuts for common operations",
  subTitle: "",
  items: [
    "create_building_config",
    "create_space_function",
    "create_floor_type",
    "create_wall_type",
    "create_window_type",
    "create_roof_type",
  ],
};

/**
 * DashboardScreen Component
 * 
 * This component displays a dashboard for students, including shortcuts for common operations, ongoing and completed courses, active assignments, and submitted building configurations.
 * It fetches data from various endpoints to populate the dashboard widgets and tables.
 * 
 * Returns:
 * @returns {JSX.Element} A JSX element containing the student dashboard.
 */
export const DashboardScreen = () => {
  const [assignmentsList, setAssignmentsList] = useState<
    ActiveAssignmentsTree[]
  >([]);
  const [coursesInfo, setCoursesInfo] = useState<CourseInfo[]>([]);
  const { userId } = useUserContext();
  useEffect(() => {
    application
      .getHttpClient()
      .get(
        `analyticsms/course-analytics/get-active-assignments-by-student-id?userId=${userId}`
      )
      .then((res: HttpResponse) => {
        const groupedData: ActiveAssignmentsTree[] = [];

        const findCourse = (courseId: string) => {
          return groupedData.find(
            (course: any) => course.courseId === courseId
          );
        };

        for (const item of res.body) {
          let course = findCourse(item.courseId);
          if (!course) {
            course = {
              courseId: item.courseId,
              courseName: item.courseName,
              assignments: [],
            };
            groupedData.push(course);
          }

          course.assignments.push({
            assignmentId: item.assignmentId,
            title: item.title,
          });
        }

        setAssignmentsList(groupedData);
      });

    application
      .getHttpClient()
      .get(
        `analyticsms/course-analytics/get-courses-info-by-user-id?userId=${userId}`
      )
      .then((res: HttpResponse) => {
        setCoursesInfo(res.body);
      });
  }, []);

  const tableProps: any = {
    source: {
      type: "REMOTE",
      url: "/coursesms/assignments/submissions/summary",
      urlParams: {
        UserId: userId,
      },
    },
    columns: [
      {
        id: "custom",
        text: "Assignment Title",
        dataField: "assignment.title",
        sort: false,
      },
      {
        id: "custom",
        text: "Configuration Name",
        dataField: "configuration.name",
        sort: false,
      },
      {
        id: "createdDate",
        text: "Submitted Date",
      },
    ],
    showPagination: true,
    showRowActions: false,
    options: {
      pagination: true,
    },
  };

  return (
    <Page title="Dashboard">
      <Row>
        <Col sm="12" md="4">
          <Row className="m-0 h-100">
            <WidgetQuickActions {...actionsWidget} />
          </Row>
        </Col>
        <Col sm="12" md="4">
          <Row className="mx-0">
            <Widget title="Ongoing courses" subTitle="">
              <SimpleTable
                tableItems={coursesInfo}
                filterPredicate={(course) => course.state === "ACTIVE"}
              />
            </Widget>
          </Row>
        </Col>
        <Col sm="12" md="4">
          <Row className="mx-0">
            <Widget title="Completed courses" subTitle="">
              <SimpleTable
                tableItems={coursesInfo}
                filterPredicate={(course) => course.state === "COMPLETED"}
              />
            </Widget>
          </Row>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col sm="12" md="4">
          <Row className="mx-0 h-100">
            <Widget title="Shortcuts for your active assignments" subTitle="">
              <div>
                {assignmentsList.length === 0 ? (
                  <p>No active assignments</p>
                ) : (
                  assignmentsList.map((data: ActiveAssignmentsTree) => (
                    <ul key={data.courseId} className="list-styled">
                      <li>
                        <Link to={`/courses/${data.courseId}/assignments`}>
                          {data.courseName}
                        </Link>
                        <ul>
                          {data.assignments.map((assignment) => (
                            <li key={assignment.assignmentId}>
                              <Link
                                to={`/assignments/${data.courseId}/${assignment.assignmentId}`}
                              >
                                {assignment.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                    </ul>
                  ))
                )}
              </div>
            </Widget>
          </Row>
        </Col>
        <Col>
          <Widget title="Building configurations submitted" subTitle="">
            <RootieTable {...tableProps} />
          </Widget>
        </Col>
      </Row>
    </Page>
  );
};

/**
 * Dashboard Route Configuration
 * 
 * This object configures the route for the dashboard screen.
 * It sets the component to be rendered as `DashboardScreen` and the layout as `layoutWorkspace`.
 */
export const dashboardRoute = {
  ...studentsAreaRoutes.dashboard,
  screen: {
    view: {
      component: DashboardScreen,
    },
    layout: layoutWorkspace,
  },
};
